/**
 * ocrRois.ts — Push 4 (v1.4.1 b8)
 *
 * Single source of truth for normalized OCR regions of interest (ROIs).
 *
 * Why this module exists
 * ──────────────────────
 * Before Push 4, every per-screen OCR module hard-coded its own crop
 * percentages inside `cropPct(img, 0.20, 0.04, 0.60, 0.06)`-style calls.
 * That made it impossible to:
 *   1. Move CP reading off the "broad text band" model — each module had
 *      its own ad-hoc band, with no shared notion of "the CP banner is
 *      *here* and nothing else."
 *   2. Pass the same ROI to a native (Apple Vision / ML Kit) region-OCR
 *      bridge — the bridge needs structured rects, not buried magic
 *      numbers.
 *   3. Audit which fields are ROI-driven vs full-frame.
 *
 * This module exposes the normalized rects (x, y, w, h ∈ [0,1]) for every
 * commit-critical field. Per-screen modules import from here and call
 * `cropPctFromRoi(img, ROI)` instead of duplicating numbers. A future
 * native bridge can iterate the exported map and call Apple Vision's
 * `regionOfInterest` API directly per ROI.
 *
 * Tight CP band rationale (Push 4 deep-scan rebuild)
 * ──────────────────────────────────────────────────
 * The CP banner on a Pokémon GO detail page is a SINGLE numeric value at
 * the top-center of the screen. A "broad text band" that spans y 0.02-0.10
 * and x 0.05-0.95 will pick up tag chips, the year/month badge, even box
 * CP pills if the page was actually misrouted from box. Push 4's deep-scan
 * model (Poke Genie / CalcyIV) reads ONLY the CP banner pixels — a 40%-wide
 * top-center crop with a max height of 7% of screen — and refuses to fall
 * back to wider crops when the tight crop is empty. If the tight crop is
 * empty, CP is null, full stop. That eliminates the "Happiny shown with
 * Gimmighoul's CP" failure class.
 *
 * Pure data + a single helper. Safe to import from Node tests.
 */

export interface NormalizedRoi {
  /** Normalized x (0..1) of the crop's left edge. */
  x: number;
  /** Normalized y (0..1) of the crop's top edge. */
  y: number;
  /** Normalized width (0..1). */
  w: number;
  /** Normalized height (0..1). */
  h: number;
}

export interface RoiCatalogEntry {
  /** Stable id, e.g. "detail.cpBanner". Used by the native bridge map. */
  id: string;
  /** The normalized rect itself. */
  roi: NormalizedRoi;
  /**
   * Suggested OCR character whitelist. The native bridge does not honour
   * Tesseract whitelists, but a forthcoming Apple Vision custom-words list
   * can use it as input.
   */
  whitelist?: string;
  /** Free-text label for diagnostics panels. */
  label: string;
}

// ── Detail-screen ROIs (Push 4 tight bands) ────────────────────────────────

/**
 * CP banner — tight top-center band only. The detail page's CP banner
 * occupies roughly x 0.35-0.65, y 0.045-0.085 on a 9:19.5 iOS screenshot.
 * We pad slightly to absorb DPI drift but DELIBERATELY keep the band
 * narrow so a misrouted box page (whose CP pills sit on the left edge)
 * cannot leak into the read.
 *
 * Push 4 contract: the deep-scan path MUST use this ROI for CP. The
 * legacy "wide CP fallback" in detailOcr.ts is now opt-in via
 * `allowWideCpFallback: true` and is gated on a low-confidence appraisal
 * follow-up. The default behavior refuses to widen.
 */
export const DETAIL_CP_BANNER_ROI: RoiCatalogEntry = {
  // Push 4 follow-up (v1.4.1 b9): widened from x:0.30 w:0.40 h:0.06 to
  // x:0.25 w:0.50 h:0.075 after QA screenshots (IMG_6582, IMG_6581) showed
  // the Pokémon GO CP banner sometimes sits a touch wider (long-CP species
  // like Mega-Mewtwo "MegaPowered CP4327") and the previous narrow band
  // clipped the trailing digit. The widened band still lives under the
  // iOS status bar / Dynamic Island and well above the sprite, so a
  // misrouted box page still cannot leak its left-column CP pills into
  // the read.
  id: "detail.cpBanner",
  roi: { x: 0.25, y: 0.035, w: 0.50, h: 0.075 },
  whitelist: "CcPp0123456789,",
  label: "Detail CP banner (top-center, tight)",
};

/**
 * Sprite area — used for visual flag sampling, not text OCR. We surface
 * it here so it lives in the same catalog and a future native ROI vision
 * pass can read it as a single rect.
 */
export const DETAIL_SPRITE_ROI: RoiCatalogEntry = {
  id: "detail.sprite",
  roi: { x: 0.18, y: 0.10, w: 0.64, h: 0.28 },
  label: "Detail sprite area",
};

/** Name / nickname band on the detail page. */
export const DETAIL_NAME_ROI: RoiCatalogEntry = {
  id: "detail.name",
  roi: { x: 0.10, y: 0.39, w: 0.80, h: 0.07 },
  label: "Detail name / nickname",
};

/** HP / level text band. */
export const DETAIL_HP_ROI: RoiCatalogEntry = {
  id: "detail.hp",
  roi: { x: 0.20, y: 0.45, w: 0.60, h: 0.05 },
  whitelist: "0123456789/HP",
  label: "Detail HP",
};

/** Appraisal IV bars card (LEFT half of lower screen). */
export const DETAIL_APPRAISAL_CARD_ROI: RoiCatalogEntry = {
  id: "detail.appraisalCard",
  roi: { x: 0.05, y: 0.74, w: 0.50, h: 0.14 },
  label: "Detail appraisal IV bars card",
};

// ── Box ROIs ───────────────────────────────────────────────────────────────

/** Whole box grid (3 cols x 5 rows in the current PoGo light UI). */
export const BOX_GRID_ROI: RoiCatalogEntry = {
  id: "box.grid",
  roi: { x: 0.0, y: 0.18, w: 1.0, h: 0.72 },
  label: "Box grid",
};

// ── Pokedex ROIs ───────────────────────────────────────────────────────────

/** Pokedex grid (used for dex-state-only OCR). */
export const POKEDEX_GRID_ROI: RoiCatalogEntry = {
  id: "pokedex.grid",
  roi: { x: 0.0, y: 0.18, w: 1.0, h: 0.72 },
  label: "Pokedex grid",
};

// ── Catalog ────────────────────────────────────────────────────────────────

/**
 * The complete catalog of commit-critical ROIs. A future native ROI bridge
 * iterates this map and asks the OS-level vision API to OCR each rect
 * separately, returning a `Record<roiId, recognizedText>`. Until that
 * bridge ships, the per-screen JS modules read this catalog and crop
 * locally.
 */
export const ROI_CATALOG: Readonly<Record<string, RoiCatalogEntry>> = Object.freeze({
  [DETAIL_CP_BANNER_ROI.id]: DETAIL_CP_BANNER_ROI,
  [DETAIL_SPRITE_ROI.id]: DETAIL_SPRITE_ROI,
  [DETAIL_NAME_ROI.id]: DETAIL_NAME_ROI,
  [DETAIL_HP_ROI.id]: DETAIL_HP_ROI,
  [DETAIL_APPRAISAL_CARD_ROI.id]: DETAIL_APPRAISAL_CARD_ROI,
  [BOX_GRID_ROI.id]: BOX_GRID_ROI,
  [POKEDEX_GRID_ROI.id]: POKEDEX_GRID_ROI,
});

